/**
 * NEXUS UI - Loader
 * Futuristic loading indicators
 */

import React from 'react';
import type { BaseProps, NexusVariant, NexusSize } from './types';

type LoaderType = 'ring' | 'hexagon' | 'pulse' | 'orbit';

export interface LoaderProps extends BaseProps {
  type?: LoaderType;
  variant?: NexusVariant;
  size?: NexusSize;
  label?: string;
}

const variantColors: Record<NexusVariant, { primary: string; secondary: string; glow: string }> = {
  cyan: { primary: '#00d4ff', secondary: '#0d4f5c', glow: 'rgba(0, 212, 255, 0.6)' },
  blue: { primary: '#3b82f6', secondary: '#1a3a5c', glow: 'rgba(59, 130, 246, 0.6)' },
  violet: { primary: '#a855f7', secondary: '#3b1a5c', glow: 'rgba(168, 85, 247, 0.6)' },
  green: { primary: '#00ff88', secondary: '#0d4a2a', glow: 'rgba(0, 255, 136, 0.6)' },
  amber: { primary: '#ffaa00', secondary: '#5c3d0d', glow: 'rgba(255, 170, 0, 0.6)' },
  red: { primary: '#ff3366', secondary: '#5c1a1a', glow: 'rgba(255, 51, 102, 0.6)' },
};

const sizeConfig: Record<NexusSize, number> = {
  sm: 24,
  md: 40,
  lg: 56,
  xl: 72,
};

export const Loader: React.FC<LoaderProps> = ({
  type = 'ring',
  variant = 'cyan',
  size = 'md',
  label,
  className = '',
  style,
}) => {
  const colors = variantColors[variant];
  const dimension = sizeConfig[size];

  const renderLoader = () => {
    switch (type) {
      case 'ring':
        return (
          <div
            style={{
              position: 'relative',
              width: dimension,
              height: dimension,
            }}
          >
            {/* Outer ring */}
            <div
              style={{
                position: 'absolute',
                inset: 0,
                border: `2px solid ${colors.secondary}`,
                borderTopColor: 'transparent',
                borderLeftColor: colors.primary,
                borderRadius: '50%',
                animation: 'nx-rotate-reverse 1.5s linear infinite',
              }}
            />
            {/* Inner ring */}
            <div
              style={{
                position: 'absolute',
                inset: '6px',
                border: `2px solid ${colors.secondary}`,
                borderTopColor: colors.primary,
                borderRadius: '50%',
                animation: 'nx-rotate 1s linear infinite',
                boxShadow: `0 0 8px ${colors.glow}`,
              }}
            />
          </div>
        );

      case 'hexagon':
        return (
          <div
            style={{
              width: dimension,
              height: dimension,
              clipPath: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)',
              background: `conic-gradient(from 0deg, ${colors.primary}, ${colors.secondary}, ${colors.primary})`,
              animation: 'nx-rotate 2s linear infinite',
              boxShadow: `0 0 12px ${colors.glow}`,
            }}
          >
            <div
              style={{
                position: 'absolute',
                inset: '3px',
                clipPath: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)',
                background: 'var(--nx-void-panel)',
              }}
            />
          </div>
        );

      case 'pulse':
        return (
          <div
            style={{
              position: 'relative',
              width: dimension,
              height: dimension,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                style={{
                  position: 'absolute',
                  width: '100%',
                  height: '100%',
                  border: `2px solid ${colors.primary}`,
                  borderRadius: '50%',
                  animation: `nx-ripple 1.5s ease-out infinite ${i * 0.4}s`,
                }}
              />
            ))}
            <div
              style={{
                width: dimension / 4,
                height: dimension / 4,
                borderRadius: '50%',
                background: colors.primary,
                boxShadow: `0 0 12px ${colors.glow}`,
              }}
            />
          </div>
        );

      case 'orbit':
        return (
          <div
            style={{
              position: 'relative',
              width: dimension,
              height: dimension,
            }}
          >
            {/* Center */}
            <div
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: dimension / 5,
                height: dimension / 5,
                borderRadius: '50%',
                background: colors.primary,
                boxShadow: `0 0 12px ${colors.glow}`,
              }}
            />
            {/* Orbits */}
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                style={{
                  position: 'absolute',
                  inset: i * 6,
                  border: `1px solid ${colors.secondary}`,
                  borderRadius: '50%',
                  animation: `nx-rotate ${2 + i * 0.5}s linear infinite`,
                }}
              >
                <div
                  style={{
                    position: 'absolute',
                    top: '-4px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: 8 - i * 2,
                    height: 8 - i * 2,
                    borderRadius: '50%',
                    background: colors.primary,
                    boxShadow: `0 0 8px ${colors.glow}`,
                  }}
                />
              </div>
            ))}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div
      className={`nx-loader ${className}`}
      style={{
        display: 'inline-flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '12px',
        ...style,
      }}
    >
      {renderLoader()}
      {label && (
        <span
          style={{
            fontFamily: 'var(--nx-font-interface)',
            fontSize: '12px',
            textTransform: 'uppercase',
            letterSpacing: '0.1em',
            color: 'var(--nx-text-secondary)',
            animation: 'nx-pulse 2s ease-in-out infinite',
          }}
        >
          {label}
        </span>
      )}
    </div>
  );
};

export default Loader;
